import React, { useEffect, useState } from "react";
import { Tooltip } from "antd";
import { RobotOutlined, UserOutlined, ClockCircleOutlined, MinusOutlined } from "@ant-design/icons";

import config from "../../../../config";
import "./handledBy.css";

/**
 * Who produced this breakdown or estimate.
 *
 * Restricted to super admins and covering FTC personnel — but this component is
 * NOT the gate. The server omits the data entirely for anyone else and answers
 * 403 on the endpoint, so hiding it here is presentation, not security. Treat any
 * change to it accordingly.
 *
 * Renders nothing at all when the caller may not see it, rather than an empty
 * placeholder, so the surface looks identical to an office user.
 *
 * Two shapes:
 *   <HandledByChip value={payloadFromAListResponse} />   — already fetched
 *   <HandledByChip kind="breakdown" id={42} />           — fetch it itself
 */
export default function HandledByChip({ value, kind, id, compact }) {
  const [fetched, setFetched] = useState(undefined);

  const shouldFetch = value === undefined && kind && id;

  useEffect(
    function () {
      if (!shouldFetch) return undefined;
      let cancelled = false;
      (async function () {
        try {
          const user = JSON.parse(localStorage.getItem("user"));
          const headers = user && user.key ? { Authorization: "Token  " + user.key } : {};
          const res = await fetch(
            `${config.apiGateway.URL}/clinics/handled-by/?kind=${encodeURIComponent(kind)}&id=${encodeURIComponent(id)}`,
            { method: "GET", headers },
          );
          // 403 is the ordinary answer for most users — not an error worth
          // reporting, just nothing to show.
          if (cancelled) return;
          setFetched(res.ok ? await res.json() : null);
        } catch (e) {
          if (!cancelled) setFetched(null);
        }
      })();
      return function () { cancelled = true; };
    },
    [shouldFetch, kind, id],
  );

  const data = value !== undefined ? value : fetched;

  // undefined = still loading or never asked; null = not permitted / nothing.
  if (data === undefined || data === null) return null;

  const actor = data.actor;
  const via = data.via;

  let icon = <MinusOutlined />;
  let text = data.via_label || "Not recorded";
  let tone = "muted";

  if (actor) {
    icon = via === "agent_run" ? <RobotOutlined /> : <UserOutlined />;
    // Most accounts have no first/last name, so `name` falls back to the email —
    // show its local part ("kooshag77"), which carries the identity; the full
    // address stays in the tooltip. A real name has no "@" and passes through.
    const at = (actor.name || "").indexOf("@");
    text = at > 0 ? actor.name.slice(0, at) : actor.name;
    tone = via === "agent_run" ? "agent" : "person";
  } else if (via === "scheduled") {
    icon = <ClockCircleOutlined />;
    text = "Ran automatically";
  }

  // The tooltip carries the rest: the role, how it was produced, and when. The
  // chip itself stays one line so it can sit in a table cell.
  const detail = [
    actor ? actor.name : null,
    actor ? actor.role_label : null,
    data.via_label,
    data.source_label,
    data.at ? new Date(data.at).toLocaleString() : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Tooltip title={detail}>
      <span className={"hb-chip hb-chip--" + tone + (compact ? " hb-chip--compact" : "")}>
        <span className="hb-chip-icon">{icon}</span>
        <span className="hb-chip-name">{text}</span>
        {actor && !compact ? (
          <span className="hb-chip-role">{actor.role_label}</span>
        ) : null}
      </span>
    </Tooltip>
  );
}
